import { html, nothing } from "lit";
import {
  signInWithGoogleOAuth,
  initGoogleAuthListener,
} from "../google-auth.ts";
import { ConnectErrorDetailCodes } from "../../../../src/gateway/protocol/connect-error-details.js";
import { t } from "../../i18n/index.ts";
import type { AppViewState } from "../app-view-state.ts";
import { buildExternalLinkRel, EXTERNAL_LINK_TARGET } from "../external-link.ts";
import { icons } from "../icons.ts";
import { normalizeBasePath } from "../navigation.ts";
import { normalizeLowercaseStringOrEmpty } from "../string-coerce.ts";
import { agentLogoUrl } from "./agents-utils.ts";
import { renderConnectCommand } from "./connect-command.ts";
import {
  resolveAuthHintKind,
  resolvePairingHint,
  shouldShowInsecureContextHint,
} from "./overview-hints.ts";

type LoginFailureKind =
  | "auth-required"
  | "auth-failed"
  | "auth-rate-limited"
  | "pairing-required"
  | "insecure-context"
  | "origin-not-allowed"
  | "protocol-mismatch"
  | "network";

export type LoginFailureFeedback = {
  kind: LoginFailureKind;
  title: string;
  summary: string;
  steps: string[];
  docsHref: string;
  docsLabel: string;
  rawError: string;
};

type LoginFailureFeedbackParams = {
  connected: boolean;
  lastError: string | null;
  lastErrorCode?: string | null;
  hasToken: boolean;
  hasPassword: boolean;
};

function resolveDocsLabel(href: string): string {
  if (href.includes("insecure-http")) {
    return t("login.failure.docsInsecure");
  }
  if (href.includes("device-pairing")) {
    return t("login.failure.docsPairing");
  }
  return t("login.failure.docsAuth");
}

function redactLoginFailureError(value: string): string {
  return value
    .replace(
      /([?#&])(?:access_token|auth|deviceToken|password|refresh_token|token)=([^&#\s]+)/gi,
      "$1[redacted-credential]",
    )
    .replace(/\bBearer\s+([A-Za-z0-9._~+/-]+=*)/gi, "Bearer [redacted]")
    .replace(
      /(["']?(?:access|accessToken|deviceToken|password|refresh|refreshToken|token)["']?\s*[:=]\s*)["']?[^"',\s}]+/gi,
      "$1[redacted]",
    );
}

function buildFeedback(params: {
  kind: LoginFailureKind;
  rawError: string;
  docsHref?: string;
  titleKey: string;
  summaryKey: string;
  stepKeys: string[];
  stepParams?: Record<string, string>;
}): LoginFailureFeedback {
  const docsHref = params.docsHref ?? "https://docs.ptolemy.ai/web/dashboard";
  return {
    kind: params.kind,
    title: t(params.titleKey, params.stepParams),
    summary: t(params.summaryKey, params.stepParams),
    steps: params.stepKeys.map((key) => t(key, params.stepParams)),
    docsHref,
    docsLabel: resolveDocsLabel(docsHref),
    rawError: redactLoginFailureError(params.rawError),
  };
}

export function resolveLoginFailureFeedback(
  params: LoginFailureFeedbackParams,
): LoginFailureFeedback | null {
  if (params.connected || !params.lastError) {
    return null;
  }

  const rawError = params.lastError;
  const lastErrorCode = params.lastErrorCode ?? null;
  const lower = normalizeLowercaseStringOrEmpty(rawError);

  const pairing = resolvePairingHint(false, rawError, lastErrorCode);
  if (pairing) {
    return buildFeedback({
      kind: "pairing-required",
      rawError,
      docsHref: "https://docs.ptolemy.ai/web/control-ui#device-pairing-first-connection",
      titleKey:
        pairing.kind === "scope-upgrade-pending"
          ? "login.failure.pairing.scopeTitle"
          : pairing.kind === "role-upgrade-pending"
            ? "login.failure.pairing.roleTitle"
            : pairing.kind === "metadata-upgrade-pending"
              ? "login.failure.pairing.metadataTitle"
              : "login.failure.pairing.title",
      summaryKey:
        pairing.kind === "pairing-required"
          ? "login.failure.pairing.summary"
          : "login.failure.pairing.upgradeSummary",
      stepKeys: [
        "login.failure.pairing.stepList",
        pairing.requestId
          ? "login.failure.pairing.stepApproveId"
          : "login.failure.pairing.stepApprove",
        "login.failure.pairing.stepReconnect",
      ],
      stepParams: { requestId: pairing.requestId ?? "" },
    });
  }

  if (
    lastErrorCode === ConnectErrorDetailCodes.AUTH_RATE_LIMITED ||
    lower.includes("too many failed authentication attempts") ||
    lower.includes("rate limit")
  ) {
    return buildFeedback({
      kind: "auth-rate-limited",
      rawError,
      titleKey: "login.failure.rateLimited.title",
      summaryKey: "login.failure.rateLimited.summary",
      stepKeys: [
        "login.failure.rateLimited.stepStop",
        "login.failure.rateLimited.stepWait",
        "login.failure.rateLimited.stepCheckClients",
      ],
    });
  }

  if (shouldShowInsecureContextHint(false, rawError, lastErrorCode)) {
    return buildFeedback({
      kind: "insecure-context",
      rawError,
      docsHref: "https://docs.ptolemy.ai/web/control-ui#insecure-http",
      titleKey: "login.failure.insecure.title",
      summaryKey: "login.failure.insecure.summary",
      stepKeys: [
        "login.failure.insecure.stepHttps",
        "login.failure.insecure.stepLocalCompat",
        "login.failure.insecure.stepAvoidDisable",
      ],
    });
  }

  if (
    lastErrorCode === ConnectErrorDetailCodes.CONTROL_UI_ORIGIN_NOT_ALLOWED ||
    lower.includes("origin not allowed")
  ) {
    return buildFeedback({
      kind: "origin-not-allowed",
      rawError,
      docsHref:
        "https://docs.ptolemy.ai/web/control-ui#debuggingtesting-dev-server--remote-gateway",
      titleKey: "login.failure.origin.title",
      summaryKey: "login.failure.origin.summary",
      stepKeys: [
        "login.failure.origin.stepAllowedOrigins",
        "login.failure.origin.stepFullOrigin",
        "login.failure.origin.stepRestart",
      ],
    });
  }

  if (lower.includes("protocol mismatch")) {
    return buildFeedback({
      kind: "protocol-mismatch",
      rawError,
      docsHref:
        "https://docs.ptolemy.ai/web/control-ui#debuggingtesting-dev-server--remote-gateway",
      titleKey: "login.failure.protocol.title",
      summaryKey: "login.failure.protocol.summary",
      stepKeys: [
        "login.failure.protocol.stepDashboard",
        "login.failure.protocol.stepDevUi",
        "login.failure.protocol.stepRestart",
      ],
    });
  }

  const authHintKind = resolveAuthHintKind({
    connected: false,
    lastError: rawError,
    lastErrorCode,
    hasToken: params.hasToken,
    hasPassword: params.hasPassword,
  });
  if (authHintKind === "required") {
    return buildFeedback({
      kind: "auth-required",
      rawError,
      titleKey: "login.failure.authRequired.title",
      summaryKey: "login.failure.authRequired.summary",
      stepKeys: [
        "login.failure.authRequired.stepPaste",
        "login.failure.authRequired.stepGenerate",
        "login.failure.authRequired.stepConnect",
      ],
    });
  }
  if (authHintKind === "failed") {
    return buildFeedback({
      kind: "auth-failed",
      rawError,
      titleKey: "login.failure.authFailed.title",
      summaryKey: "login.failure.authFailed.summary",
      stepKeys: [
        "login.failure.authFailed.stepDashboard",
        "login.failure.authFailed.stepReplace",
        "login.failure.authFailed.stepMode",
      ],
    });
  }

  return buildFeedback({
    kind: "network",
    rawError,
    titleKey: "login.failure.network.title",
    summaryKey: "login.failure.network.summary",
    stepKeys: [
      "login.failure.network.stepGateway",
      "login.failure.network.stepUrl",
      "login.failure.network.stepDashboard",
    ],
  });
}

function renderLoginFailure(feedback: LoginFailureFeedback) {
  return html`
    <div
      class="callout danger login-gate__failure"
      role="alert"
      aria-live="polite"
      data-kind=${feedback.kind}
    >
      <div class="login-gate__failure-title">${feedback.title}</div>
      <div class="login-gate__failure-summary">${feedback.summary}</div>
      <ol class="login-gate__failure-steps">
        ${feedback.steps.map((step) => html`<li>${step}</li>`)}
      </ol>
      <details class="login-gate__failure-detail">
        <summary>${t("login.failure.rawError")}</summary>
        <div class="login-gate__failure-raw mono">${feedback.rawError}</div>
      </details>
      <a
        class="session-link login-gate__failure-docs"
        href=${feedback.docsHref}
        target=${EXTERNAL_LINK_TARGET}
        rel=${buildExternalLinkRel()}
        >${feedback.docsLabel}</a
      >
    </div>
  `;
}

let googleSigningIn = false;
let googleAuthError: string | null = null;
let googleAuthSuccess: string | null = null;
let googleListenerInitialized = false;

export function renderLoginGate(state: AppViewState) {
  const basePath = normalizeBasePath(state.basePath ?? "");
  const faviconSrc = agentLogoUrl(basePath);
  const failure = resolveLoginFailureFeedback({
    connected: state.connected,
    lastError: state.lastError,
    lastErrorCode: state.lastErrorCode,
    hasToken: Boolean(state.settings.token.trim()),
    hasPassword: Boolean(state.password.trim()),
  });

  const updatable = state as AppViewState & { requestUpdate?: () => void };

  if (typeof window !== "undefined" && !googleListenerInitialized) {
    googleListenerInitialized = true;
    initGoogleAuthListener((session) => {
      if (session.ok && session.token && !state.connected) {
        state.applySettings({ ...state.settings, token: session.token });
        googleAuthSuccess = `Session restored (${session.email})`;
        updatable.requestUpdate?.();
        state.connect();
      }
    });
  }

  const onGoogleSignInClick = async () => {
    if (googleSigningIn) return;
    googleSigningIn = true;
    googleAuthError = null;
    googleAuthSuccess = null;
    updatable.requestUpdate?.();

    try {
      const res = await signInWithGoogleOAuth();
      if (res.ok && res.token) {
        googleAuthSuccess = `Signed in as ${res.email}. Connecting to Gateway…`;
        state.applySettings({ ...state.settings, token: res.token });
        googleSigningIn = false;
        updatable.requestUpdate?.();
        state.connect();
      } else {
        googleSigningIn = false;
        googleAuthError = res.error || "Authentication failed. Authorized accounts only.";
        updatable.requestUpdate?.();
      }
    } catch (err: unknown) {
      googleSigningIn = false;
      googleAuthError = err instanceof Error ? err.message : String(err);
      updatable.requestUpdate?.();
    }
  };

  // For the sovereign OmniAccount portal, arriving unauthenticated is expected.
  // We only display infrastructure/origin errors on the main card.
  // Manual token credential feedback and pairing hints are placed inside the manual access accordion.
  const showMainFailure =
    failure &&
    failure.kind !== "auth-required" &&
    failure.kind !== "auth-failed" &&
    failure.kind !== "pairing-required";
  const manualFailure =
    failure && (failure.kind === "auth-required" || failure.kind === "auth-failed" || failure.kind === "pairing-required") ? failure : null;

  return html`
    <div class="login-gate">
      <div class="login-gate__card">
        <div class="login-gate__header">
          <div class="login-gate__avatar-wrap">
            <img
              class="login-gate__avatar-img"
              src="${basePath ? `${basePath}/avatar-ptolemy.png` : "/avatar-ptolemy.png"}"
              alt="Ptolemy"
            />
          </div>
          <div class="login-gate__title">Ptolemy’s Kingdom Portal</div>
          <div class="login-gate__badge">Omnia Intelligence &amp; Sovereign OmniAccount Gateway</div>
          <p class="login-gate__mission">
            The sovereign nervous system of the Ptolemy Kingdom.
            Authorized access only.
          </p>
        </div>

        <button
          type="button"
          class="login-gate__google-btn"
          @click=${onGoogleSignInClick}
          ?disabled=${googleSigningIn}
          aria-busy=${googleSigningIn}
          aria-label="Continue with Google"
        >
          ${googleSigningIn
            ? html`
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" style="animation: spin 0.8s linear infinite;">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
                <span>Signing in with Google…</span>
              `
            : html`
                <svg width="18" height="18" viewBox="0 0 24 24" style="flex-shrink: 0">
                  <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.66-5.17 3.66-9.17z"/>
                  <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.24v3.15C3.26 21.36 7.34 24 12 24z"/>
                  <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.24C.45 8.15 0 9.99 0 12s.45 3.85 1.24 5.42l4.04-3.15z"/>
                  <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.34 0 3.26 2.64 1.24 6.58l4.04 3.15c.95-2.83 3.6-4.98 6.72-4.98z"/>
                </svg>
                <span>Continue with Google</span>
              `}
        </button>

        ${googleAuthSuccess
          ? html`<div class="login-gate__success-banner">${googleAuthSuccess}</div>`
          : nothing}

        ${googleAuthError
          ? html`<div class="login-gate__error-banner" role="alert">${googleAuthError}</div>`
          : nothing}

        <div class="login-gate__allowlist-badge">
          Authorized Accounts: <strong>ryan@, bill@, ptolemy@omniatheatre.com</strong>
        </div>

        ${showMainFailure ? renderLoginFailure(failure) : nothing}

        <details class="login-gate__manual-card">
          <summary class="login-gate__manual-summary">
            Manual Token / Developer Access
          </summary>
          <div class="login-gate__form">
            ${manualFailure ? renderLoginFailure(manualFailure) : nothing}
            <label class="field">
              <span>${t("overview.access.wsUrl")}</span>
              <input
                .value=${state.settings.gatewayUrl}
                @input=${(e: Event) => {
                  const v = (e.target as HTMLInputElement).value;
                  state.applySettings({ ...state.settings, gatewayUrl: v });
                }}
                placeholder="ws://127.0.0.1:18789"
              />
            </label>
            <label class="field">
              <span>${t("overview.access.token")}</span>
              <div class="login-gate__secret-row">
                <input
                  type=${state.loginShowGatewayToken ? "text" : "password"}
                  autocomplete="off"
                  spellcheck="false"
                  .value=${state.settings.token}
                  @input=${(e: Event) => {
                    const v = (e.target as HTMLInputElement).value;
                    state.applySettings({ ...state.settings, token: v });
                  }}
                  placeholder="PTOLEMY_GATEWAY_TOKEN (${t("login.passwordPlaceholder")})"
                  @keydown=${(e: KeyboardEvent) => {
                    if (e.key === "Enter") {
                      state.connect();
                    }
                  }}
                />
                <button
                  type="button"
                  class="btn btn--icon ${state.loginShowGatewayToken ? "active" : ""}"
                  title=${state.loginShowGatewayToken ? t("login.hideToken") : t("login.showToken")}
                  aria-label=${t("login.toggleTokenVisibility")}
                  aria-pressed=${state.loginShowGatewayToken}
                  @click=${() => {
                    state.loginShowGatewayToken = !state.loginShowGatewayToken;
                  }}
                >
                  ${state.loginShowGatewayToken ? icons.eye : icons.eyeOff}
                </button>
              </div>
            </label>
            <label class="field">
              <span>${t("overview.access.password")}</span>
              <div class="login-gate__secret-row">
                <input
                  type=${state.loginShowGatewayPassword ? "text" : "password"}
                  autocomplete="off"
                  spellcheck="false"
                  .value=${state.password}
                  @input=${(e: Event) => {
                    const v = (e.target as HTMLInputElement).value;
                    state.password = v;
                  }}
                  placeholder="${t("login.passwordPlaceholder")}"
                  @keydown=${(e: KeyboardEvent) => {
                    if (e.key === "Enter") {
                      state.connect();
                    }
                  }}
                />
                <button
                  type="button"
                  class="btn btn--icon ${state.loginShowGatewayPassword ? "active" : ""}"
                  title=${state.loginShowGatewayPassword
                    ? t("login.hidePassword")
                    : t("login.showPassword")}
                  aria-label=${t("login.togglePasswordVisibility")}
                  aria-pressed=${state.loginShowGatewayPassword}
                  @click=${() => {
                    state.loginShowGatewayPassword = !state.loginShowGatewayPassword;
                  }}
                >
                  ${state.loginShowGatewayPassword ? icons.eye : icons.eyeOff}
                </button>
              </div>
            </label>
            <button class="btn primary login-gate__connect" @click=${() => state.connect()}>
              ${t("common.connect")}
            </button>
          </div>
          <div class="login-gate__help">
            <div class="login-gate__help-title">${t("overview.connection.title")}</div>
            <ol class="login-gate__steps">
              <li>
                ${t("overview.connection.step1")}${renderConnectCommand("ptolemy gateway run")}
              </li>
              <li>${t("overview.connection.step2")} ${renderConnectCommand("ptolemy dashboard")}</li>
              <li>${t("overview.connection.step3")}</li>
            </ol>
            <div class="login-gate__docs">
              <a
                class="session-link"
                href="https://docs.ptolemy.ai/web/dashboard"
                target="_blank"
                rel="noreferrer"
                >${t("overview.connection.docsLink")}</a
              >
            </div>
          </div>
        </details>

        <p class="login-gate__footer">
          SPECTRE · Ptolemy Kingdom · Authorized access only
        </p>
      </div>
    </div>
  `;
}
